import React from "react";
import { Link } from "react-router-dom";
import { Box, CircularProgress, LinearProgress, Tooltip, Typography } from "@mui/material";
import { AlertTriangle, Check, FileWarning, Sparkles, UserCheck } from "lucide-react";
import { runningLabel, stageDetail, stageProgress } from "./ParseStatusPanel";
import { brandedStageDetail, brandedStageLabel, brandedStageProgress } from "./BrandedCvStatusPanel";
import { statusAccent } from "./VaultFilterBar";

export const STATUS_TONE = {
  uploaded: { label: "Uploaded", color: "hsl(150 10% 40%)", bg: "hsl(140 10% 94%)" },
  extracting_text: { label: "Reading file", color: "hsl(150 10% 40%)", bg: "hsl(140 10% 94%)" },
  ready: { label: "Ready to extract", color: "hsl(35 92% 30%)", bg: "hsl(40 100% 94%)" },
  ocring: { label: "Reading the scan", color: "hsl(217 70% 35%)", bg: "hsl(217 91% 95%)" },
  needs_ocr: { label: "Nothing legible", color: "hsl(35 92% 30%)", bg: "hsl(40 100% 94%)" },
  queued: { label: "Queued", color: "hsl(217 70% 35%)", bg: "hsl(217 91% 95%)" },
  parsing: { label: "Extracting", color: "hsl(217 70% 35%)", bg: "hsl(217 91% 95%)" },
  parsed: { label: "Extracted", color: "hsl(142 71% 25%)", bg: "hsl(142 60% 93%)" },
  converted: { label: "Candidate created", color: "hsl(150 38% 17%)", bg: "hsl(140 30% 94%)" },
  failed: { label: "Failed", color: "hsl(0 72% 45%)", bg: "hsl(0 80% 96%)" },
  cancelled: { label: "Cancelled", color: "hsl(150 10% 40%)", bg: "hsl(140 10% 94%)" },
};

const relativeTime = (value) => {
  if (!value) return "";
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (Number.isNaN(seconds)) return "";
  if (seconds < 60) return "now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

const CircleCheck = ({ checked, onClick, label }) => (
  <Box
    component="button"
    type="button"
    aria-label={label}
    aria-pressed={checked}
    onClick={onClick}
    sx={{
      all: "unset",
      width: 18,
      height: 18,
      borderRadius: "50%",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      border: "2px solid",
      borderColor: checked ? "hsl(150 38% 17%)" : "hsl(140 8% 74%)",
      backgroundColor: checked ? "hsl(150 38% 17%)" : "transparent",
      transition: "all 160ms",
      "&:hover": { borderColor: "hsl(150 38% 17%)" },
      "&:focus-visible": { outline: "2px solid hsl(150 38% 17%)", outlineOffset: 2 },
    }}
  >
    {checked && <Check size={11} strokeWidth={3} color="hsl(80 97% 72%)" />}
  </Box>
);

const secondaryLine = (item, name) => {
  if (item.status === "converted") return `Candidate #${item.candidate_id}`;
  if (item.status === "needs_ocr") return item.ocr_retryable ? "Scan came back empty" : "No text layer";
  if (item.status === "failed") return item.error || "Extraction failed";
  if (item.stalled) return "Stopped responding";

  const parsed = item.parsed || {};
  const bits = [parsed.job_title, parsed.experience_year && `${parsed.experience_year} yrs`].filter(Boolean);
  if (bits.length > 0) return bits.join(" · ");
  if (name) return item.filename;

  return STATUS_TONE[item.status]?.label || "";
};

const ROW_TONES = {
  converted: { rest: "hsl(140 4% 87%)", hover: "hsl(140 4% 84%)" },
  duplicate: { rest: "hsl(36 92% 94%)", hover: "hsl(36 88% 91%)" },
  plain: { rest: "#ffffff", hover: "hsl(140 10% 97%)" },
};

const rowTone = (item) => {
  if (item.status === "converted") return ROW_TONES.converted;
  if (item.duplicate_of) return ROW_TONES.duplicate;
  return ROW_TONES.plain;
};

const VaultListRow = ({ item, height, active, selected, showCheck, onToggleSelect, onOpen }) => {
  const parsed = item.parsed || {};
  const name = [parsed.first_name, parsed.last_name].filter(Boolean).join(" ");
  const running = ["queued", "parsing", "ocring"].includes(item.status) && !item.stalled;
  const brandedRunning = item.branded_cv_status === "generating" && !item.branded_cv_stalled;
  const accent = statusAccent(item.status, item.stalled);
  const title = name || item.filename;
  const archived = item.status === "converted";
  const tone = rowTone(item);

  return (
    <Box
      onClick={() => onOpen(item)}
      sx={{
        height,
        boxSizing: "border-box",
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: 1.5,
        pr: 1.5,
        cursor: "pointer",
        borderBottom: "1px solid hsl(140 8% 92%)",
        backgroundColor: active ? "hsla(150, 38%, 17%, 0.07)" : tone.rest,
        transition: "background-color 120ms ease",
        "&:hover": { backgroundColor: active ? "hsla(150, 38%, 17%, 0.09)" : tone.hover },
        "&:hover .vault-row-check": { opacity: 1 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          backgroundColor: accent,
          opacity: archived ? 0.35 : active || selected ? 1 : 0.65,
        }}
      />

      <Box
        className="vault-row-check"
        onClick={(event) => event.stopPropagation()}
        sx={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          width: 18,
          opacity: showCheck || selected ? 1 : 0,
          transition: "opacity 140ms ease",
        }}
      >
        <CircleCheck
          checked={selected}
          onClick={() => onToggleSelect(item.id)}
          label={`Select ${item.filename}`}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
          <Typography
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 13.5,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              color: archived ? "hsl(150 8% 48%)" : name ? "hsl(150 30% 12%)" : "hsl(150 12% 38%)",
            }}
          >
            {title}
          </Typography>

          {item.duplicate_of && !archived && (
            <Tooltip
              arrow
              placement="top"
              title={`Matches another candidate's email: ${item.duplicate_of.name}`}
            >
              <Box
                component={Link}
                to={`/internal/candidates/${item.duplicate_of.id}`}
                onClick={(event) => event.stopPropagation()}
                aria-label={`Open ${item.duplicate_of.name}`}
                sx={{ display: "flex", flexShrink: 0, alignSelf: "center", color: "inherit" }}
              >
                <AlertTriangle size={12} color="hsl(35 92% 40%)" />
              </Box>
            </Tooltip>
          )}
          {item.status === "needs_ocr" && (
            <Box sx={{ display: "flex", flexShrink: 0, alignSelf: "center" }}>
              <FileWarning size={12} color="hsl(35 92% 40%)" />
            </Box>
          )}
          {item.status === "converted" && (
            <Box sx={{ display: "flex", flexShrink: 0, alignSelf: "center" }}>
              <UserCheck size={12} color="hsl(150 38% 17%)" />
            </Box>
          )}
          {item.branded_cv_status && (
            <Tooltip
              arrow
              placement="top"
              title={
                item.branded_cv_status === "ready"
                  ? "Squarehub CV ready"
                  : item.branded_cv_status === "failed"
                    ? "Squarehub CV failed"
                    : brandedStageLabel(item.branded_cv_stage)
              }
            >
              <Box sx={{ display: "flex", flexShrink: 0, alignSelf: "center" }}>
                {item.branded_cv_status === "generating" && (
                  <CircularProgress size={11} thickness={6} sx={{ color: "hsl(150 38% 17%)" }} />
                )}
                {item.branded_cv_status === "ready" && <Sparkles size={12} color="hsl(150 38% 17%)" />}
                {item.branded_cv_status === "failed" && <AlertTriangle size={12} color="hsl(0 60% 45%)" />}
              </Box>
            </Tooltip>
          )}

          <Typography sx={{ fontSize: 11, flexShrink: 0, color: "hsl(150 10% 55%)" }}>
            {relativeTime(item.created_at)}
          </Typography>
        </Box>

        {running ? (
          <Box sx={{ mt: 0.5 }}>
            <Typography noWrap sx={{ fontSize: 11, color: "hsl(217 70% 35%)" }}>
              {runningLabel(item)}
              {stageDetail(item) ? ` · ${stageDetail(item)}` : ""}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={stageProgress(item)}
              sx={{
                mt: 0.5,
                height: 2,
                borderRadius: 2,
                backgroundColor: "hsl(140 8% 90%)",
                "& .MuiLinearProgress-bar": { backgroundColor: "hsl(217 91% 55%)" },
              }}
            />
          </Box>
        ) : brandedRunning ? (
          <Box sx={{ mt: 0.5 }}>
            <Typography noWrap sx={{ fontSize: 11, color: "hsl(150 30% 25%)" }}>
              {brandedStageLabel(item.branded_cv_stage)}
              {brandedStageDetail(item.branded_cv_stage, item.branded_cv_progress)
                ? ` · ${brandedStageDetail(item.branded_cv_stage, item.branded_cv_progress)}`
                : ""}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={brandedStageProgress(item.branded_cv_stage)}
              sx={{
                mt: 0.5,
                height: 2,
                borderRadius: 2,
                backgroundColor: "hsl(140 8% 90%)",
                "& .MuiLinearProgress-bar": { backgroundColor: "hsl(150 38% 17%)" },
              }}
            />
          </Box>
        ) : (
          <Typography
            noWrap
            sx={{
              fontSize: 11.5,
              mt: 0.25,
              color: item.status === "failed" || item.stalled ? "hsl(0 50% 42%)" : "hsl(150 10% 55%)",
            }}
          >
            {secondaryLine(item, name)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default VaultListRow;
